import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Phone, MapPin, Calendar, FileText, RefreshCw, Send, Trash2, Plus, FilePlus2, Pencil, Eye, CalendarClock, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { LEAD_STATUSES, getStatusMeta } from '@/lib/leadStatus';
import { formatRupeeInt, formatRupeeFixed } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PROPERTY_TYPES = ['Apartment / Flat', 'Independent / Individual House', 'Villa', 'Other'];
const LEAD_SOURCES = ['Website', 'Walk-in', 'Referral', 'Phone', 'WhatsApp', 'Instagram', 'Facebook', 'Google', 'Marketing', 'Other'];

// Reusable "date-only" picker that renders a shadcn Calendar in a Popover.
// Used everywhere a follow-up date is edited to guarantee month navigation is
// separate from date selection (the native <input type="date"> quirk).
function FollowUpDatePicker({ value, disabled, onChange, testId, className = '', label = 'Set date' }) {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? new Date(value + 'T00:00:00') : undefined;
  const displayLabel = value
    ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
    : label;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          disabled={disabled}
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`bg-white border border-stone-200 rounded px-2 py-0.5 text-[10px] tabular-nums focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:opacity-50 cursor-pointer hover:border-amber-500 transition ${className}`}
        >
          {displayLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start" onClick={(e) => e.stopPropagation()}>
        <CalendarPicker
          mode="single"
          selected={selectedDate}
          onSelect={(d) => {
            if (!d) return;
            const yr = d.getFullYear();
            const mo = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            onChange(`${yr}-${mo}-${day}`);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

// Inline follow-up date picker (shadcn Calendar in a Popover) — lets the user
// navigate months freely and only commits on explicit day click. Avoids the native
// <input type="date"> quirk where scrolling months auto-mutates the value.
function FollowUpInline({ lead, saving, onChange }) {
  const value = lead.next_follow_up || '';
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = value && value < today;
  const isToday = value && value === today;
  const cls = !value
    ? 'border-stone-200 text-stone-400 bg-white hover:bg-stone-50'
    : isOverdue ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
    : isToday ? 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
    : 'border-lime-200 bg-lime-50 text-lime-700 hover:bg-lime-100';
  const [open, setOpen] = useState(false);
  const selectedDate = value ? new Date(value + 'T00:00:00') : undefined;
  const label = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : 'Set date';
  return (
    <div data-testid={`lead-followup-wrap-${lead.lead_id}`} className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <span className="text-[9px] uppercase tracking-wide text-stone-400 font-semibold">Follow-up</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={`lead-followup-${lead.lead_id}`}
            disabled={saving}
            type="button"
            className={`relative inline-flex items-center rounded border ${cls} px-1.5 py-0.5 transition cursor-pointer disabled:opacity-50`}
          >
            <CalendarClock className="w-3 h-3 mr-1 opacity-70" />
            <span className="text-[10px] font-medium tabular-nums">{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start" onClick={(e) => e.stopPropagation()}>
          <CalendarPicker
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              if (!d) return;
              const yr = d.getFullYear();
              const mo = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              onChange(`${yr}-${mo}-${day}`);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {value && !saving && (
        <button
          data-testid={`lead-followup-clear-${lead.lead_id}`}
          onClick={() => onChange('')}
          type="button"
          title="Clear follow-up"
          className="p-0.5 rounded text-stone-400 hover:text-red-600 hover:bg-stone-50"
        >
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

// Inline status badge button + popover (top-level to satisfy react/no-unstable-nested-components)
function InlineStatusBadge({ lead, onChangeStatus }) {
  const meta = getStatusMeta(lead.internal_status);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`lead-status-${lead.lead_id}`}
          onClick={(e) => { e.stopPropagation(); }}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap hover:shadow-sm transition ${meta.cls}`}
        >
          <span>{meta.emoji}</span>
          <span>{meta.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1.5 bg-white" align="start">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 px-2 py-1.5">Set status</div>
        {LEAD_STATUSES.map(s => (
          <button
            key={s.value}
            data-testid={`lead-status-option-${lead.lead_id}-${s.key}`}
            onClick={() => { onChangeStatus(lead, s.value); setOpen(false); }}
            className={`w-full text-left px-2 py-1.5 rounded hover:bg-stone-50 flex items-center gap-2 text-xs ${lead.internal_status === s.value ? 'bg-stone-100 font-semibold' : ''}`}
          >
            <span>{s.emoji}</span>
            <span className="flex-1">{s.label}</span>
            {lead.internal_status === s.value && <span className="text-[9px] text-amber-700">●</span>}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState({ open: false, lead: null });
  const [searchTerm, setSearchTerm] = useState('');
  // Multi-status filter — empty Set means "show all"
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  // Add-remark composer (lives in detail dialog)
  const [newRemark, setNewRemark] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingFollowUp, setSavingFollowUp] = useState(null); // lead_id currently being saved
  // Add-Lead dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', mobile: '', city: '', pincode: '', address: '', property_type: '', budget: '', source: 'Walk-in', note: '' });
  const [addSubmitting, setAddSubmitting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [lRes, qRes, tRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/leads`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/quotations`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/quotation-templates`, { withCredentials: true }),
      ]);
      setLeads(lRes.data);
      setQuotations(qRes.data);
      setTemplates(tRes.data);
    } catch (e) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const getQuotationsForLead = (leadId) => quotations.filter(q => q.lead_id === leadId);

  // Status counts for filter popover
  const statusCounts = useMemo(() => {
    const initial = Object.fromEntries(LEAD_STATUSES.map(s => [s.value, 0]));
    return leads.reduce((acc, l) => {
      const v = l.internal_status || 'New';
      return v in acc ? { ...acc, [v]: acc[v] + 1 } : acc;
    }, initial);
  }, [leads]);

  // Follow-ups needing attention:
  //  - overdue/today: next_follow_up <= today (most overdue first), AND
  //  - unset: no follow-up date captured at all (must not silently slip).
  // A lead that is "done" should be cleared via the ✓ button, not by leaving the
  // date empty — so unset always means "needs scheduling".
  const dueFollowUps = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const withDate = leads.filter(l => l.next_follow_up && l.next_follow_up <= today);
    const noDate = leads.filter(l => !l.next_follow_up);
    const dated = withDate.sort((a, b) => a.next_follow_up.localeCompare(b.next_follow_up));
    const unset = noDate.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return [...dated, ...unset];
  }, [leads]);
  const [followUpPanelCollapsed, setFollowUpPanelCollapsed] = useState(false);

  const filtered = leads.filter(l => {
    if (statusFilter.size > 0 && !statusFilter.has(l.internal_status || 'New')) return false;
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      l.name.toLowerCase().includes(q) ||
      l.mobile.includes(searchTerm) ||
      l.city.toLowerCase().includes(q)
    );
  });

  const toggleStatusFilter = (val) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  // Optimistic status change inline
  const updateStatus = async (lead, newStatus) => {
    if (lead.internal_status === newStatus || savingStatus) return;
    setSavingStatus(true);
    const prev = lead.internal_status;
    setLeads(ls => ls.map(l => l.lead_id === lead.lead_id ? { ...l, internal_status: newStatus } : l));
    if (detailDialog.lead?.lead_id === lead.lead_id) {
      setDetailDialog(d => ({ ...d, lead: { ...d.lead, internal_status: newStatus } }));
    }
    try {
      await axios.patch(`${BACKEND_URL}/api/leads/${lead.lead_id}/status`, { internal_status: newStatus }, { withCredentials: true });
      toast.success(`Status: ${newStatus}`);
    } catch (err) {
      // Revert on error
      setLeads(ls => ls.map(l => l.lead_id === lead.lead_id ? { ...l, internal_status: prev } : l));
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  // Update next follow-up date. Pass empty string to clear.
  const updateFollowUp = async (lead, newDate) => {
    const value = (newDate || '').trim();
    if ((lead.next_follow_up || '') === value) return;
    setSavingFollowUp(lead.lead_id);
    const prev = lead.next_follow_up || '';
    const apply = (l) => ({ ...l, next_follow_up: value || null });
    setLeads(ls => ls.map(l => l.lead_id === lead.lead_id ? apply(l) : l));
    if (detailDialog.lead?.lead_id === lead.lead_id) {
      setDetailDialog(d => ({ ...d, lead: apply(d.lead) }));
    }
    try {
      await axios.patch(`${BACKEND_URL}/api/leads/${lead.lead_id}/follow-up`, { next_follow_up: value }, { withCredentials: true });
      toast.success(value ? `Follow-up set: ${value}` : 'Follow-up cleared');
    } catch (err) {
      // Revert
      setLeads(ls => ls.map(l => l.lead_id === lead.lead_id ? { ...l, next_follow_up: prev || null } : l));
      toast.error(err.response?.data?.detail || 'Failed to update follow-up');
    } finally {
      setSavingFollowUp(null);
    }
  };

  const addRemark = async () => {
    const text = newRemark.trim();
    if (!text || !detailDialog.lead) return;
    setSavingRemark(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads/${detailDialog.lead.lead_id}/remarks`, { text }, { withCredentials: true });
      const updated = (l) => ({ ...l, remarks: [...(l.remarks || []), res.data] });
      setLeads(ls => ls.map(l => l.lead_id === detailDialog.lead.lead_id ? updated(l) : l));
      setDetailDialog(d => ({ ...d, lead: updated(d.lead) }));
      setNewRemark('');
      toast.success('Remark added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add remark');
    } finally { setSavingRemark(false); }
  };

  const deleteLead = async (lead, fromDialog = false) => {
    const quoteCount = getQuotationsForLead(lead.lead_id).length;
    const confirmMsg = quoteCount > 0
      ? `Delete "${lead.name}"? This will also delete ${quoteCount} associated quotation${quoteCount > 1 ? 's' : ''}. This cannot be undone.`
      : `Delete "${lead.name}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/leads/${lead.lead_id}`, { withCredentials: true });
      setLeads(ls => ls.filter(l => l.lead_id !== lead.lead_id));
      setQuotations(qs => qs.filter(q => q.lead_id !== lead.lead_id));
      if (fromDialog) setDetailDialog({ open: false, lead: null });
      toast.success(`Deleted "${lead.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const openAddLead = () => {
    setAddForm({ name: '', mobile: '', city: '', pincode: '', address: '', property_type: '', budget: '', source: 'Walk-in', note: '' });
    setAddOpen(true);
  };

  const submitAddLead = async (e) => {
    e?.preventDefault?.();
    const { name, mobile, city, pincode, property_type } = addForm;
    if (!name.trim() || !mobile.trim() || !city.trim() || !pincode.trim() || !property_type) {
      toast.error('Name, mobile, city, pincode and property type are required');
      return;
    }
    setAddSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads`, {
        ...addForm,
        budget: addForm.budget === '' ? '' : addForm.budget,
      }, { withCredentials: true });
      setLeads(ls => [res.data, ...ls]);
      setAddOpen(false);
      toast.success(`Lead "${res.data.name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lead');
    } finally {
      setAddSubmitting(false);
    }
  };

  const openQuotationForLead = (lead) => {
    navigate(`/get-quote?lead_id=${lead.lead_id}`);
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // (InlineStatusBadge moved to top-level above)

  if (loading) return <div className="text-stone-600">Loading leads...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Leads</h1>
          <p className="text-xs text-stone-500 mt-1">{leads.length} total leads · website + admin-added</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-lead-btn" size="sm" onClick={openAddLead} className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
            <Plus className="w-3 h-3 mr-1" /> Add Lead
          </Button>
          <Button data-testid="refresh-leads-btn" variant="outline" size="sm" onClick={fetchAll} className="text-xs h-8 border-stone-200">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="leads-search"
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name, mobile, or city..."
          className="w-full max-w-sm px-3 py-2 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-200"
        />
        <Popover open={statusFilterOpen} onOpenChange={setStatusFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              data-testid="lead-status-filter-btn"
              variant="outline"
              size="sm"
              className={`text-xs h-9 border-stone-200 ${statusFilter.size > 0 ? 'border-amber-300 bg-amber-50 text-amber-800' : ''}`}
            >
              Status filter
              {statusFilter.size > 0 && <span className="ml-1.5 inline-block px-1.5 rounded-full bg-amber-700 text-white text-[10px] font-bold">{statusFilter.size}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-2 bg-white" align="start">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 px-1 pb-1.5">Filter by status</div>
            {LEAD_STATUSES.map(s => {
              const checked = statusFilter.has(s.value);
              return (
                <label
                  key={s.value}
                  data-testid={`lead-filter-option-${s.key}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 cursor-pointer text-xs"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleStatusFilter(s.value)}
                    className="accent-amber-700"
                  />
                  <span>{s.emoji}</span>
                  <span className="flex-1">{s.label}</span>
                  <span className="text-[10px] text-stone-400 tabular-nums">{statusCounts[s.value] || 0}</span>
                </label>
              );
            })}
            {statusFilter.size > 0 && (
              <button
                data-testid="lead-filter-clear"
                onClick={() => setStatusFilter(new Set())}
                className="w-full mt-1 text-[11px] text-stone-600 hover:text-stone-900 py-1 border-t border-stone-100"
              >
                Clear filter
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Today's & Overdue follow-ups panel */}
      {dueFollowUps.length > 0 && (
        <div data-testid="followup-panel" className="rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-red-50/40 shadow-sm">
          <div className="flex items-center justify-between px-3 py-2 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-amber-700" />
              <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">Today&apos;s &amp; Overdue Follow-ups</span>
              <span data-testid="followup-panel-count" className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-700 text-white text-[10px] font-bold tabular-nums">{dueFollowUps.length}</span>
              {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const overdue = dueFollowUps.filter(l => l.next_follow_up && l.next_follow_up < today).length;
                const dueToday = dueFollowUps.filter(l => l.next_follow_up && l.next_follow_up === today).length;
                const unset = dueFollowUps.length - overdue - dueToday;
                return (
                  <span className="text-[10px] text-stone-600 ml-1">
                    {overdue > 0 && <span className="text-red-700 font-semibold">{overdue} overdue</span>}
                    {overdue > 0 && (dueToday > 0 || unset > 0) && <span className="text-stone-400 mx-1">·</span>}
                    {dueToday > 0 && <span className="text-amber-800 font-semibold">{dueToday} due today</span>}
                    {dueToday > 0 && unset > 0 && <span className="text-stone-400 mx-1">·</span>}
                    {unset > 0 && <span className="text-stone-500 font-semibold">{unset} not scheduled</span>}
                  </span>
                );
              })()}
            </div>
            <button
              data-testid="followup-panel-toggle"
              onClick={() => setFollowUpPanelCollapsed(c => !c)}
              className="text-[10px] text-stone-600 hover:text-stone-900 px-2 py-0.5 rounded hover:bg-white/60 transition"
            >
              {followUpPanelCollapsed ? 'Show' : 'Hide'}
            </button>
          </div>
          {!followUpPanelCollapsed && (
            <div className="divide-y divide-amber-100 max-h-72 overflow-y-auto">
              {dueFollowUps.map(lead => {
                const today = new Date().toISOString().slice(0, 10);
                const isUnset = !lead.next_follow_up;
                const isOverdue = !isUnset && lead.next_follow_up < today;
                const daysOver = isOverdue ? Math.floor((new Date(today) - new Date(lead.next_follow_up)) / (1000 * 60 * 60 * 24)) : 0;
                const remarks = lead.remarks || [];
                const latest = remarks.length > 0 ? remarks[remarks.length - 1] : null;
                const meta = getStatusMeta(lead.internal_status);
                const chipCls = isUnset
                  ? 'bg-stone-100 text-stone-600 border border-stone-300'
                  : isOverdue
                    ? 'bg-red-100 text-red-800 border border-red-200'
                    : 'bg-amber-100 text-amber-900 border border-amber-200';
                return (
                  <div key={lead.lead_id} data-testid={`followup-row-${lead.lead_id}`} className="px-3 py-2 flex items-start gap-3 hover:bg-white/60 transition">
                    <div className={`flex-shrink-0 w-14 text-center px-1.5 py-1 rounded ${chipCls}`}>
                      <div className="text-[9px] font-bold uppercase tracking-wide">{isUnset ? 'Set date' : isOverdue ? 'Overdue' : 'Today'}</div>
                      {isOverdue && <div className="text-[9px] font-bold tabular-nums">{daysOver}d</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-stone-900">{lead.name}</span>
                        <span className="text-[10px] text-stone-500 tabular-nums">{lead.mobile}</span>
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${meta.cls}`}>{meta.emoji}{meta.label}</span>
                        <span className="text-[10px] text-stone-500">· {lead.city}</span>
                        <span className="text-[10px] text-stone-500 tabular-nums">· {isUnset ? 'No date set' : `Due ${lead.next_follow_up}`}</span>
                      </div>
                      {latest && (
                        <div className="text-[10px] text-stone-600 line-clamp-1 mt-0.5">
                          <span className="text-stone-400 mr-1">Last remark:</span>{latest.text}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      <FollowUpDatePicker
                        testId={`followup-reschedule-${lead.lead_id}`}
                        value={lead.next_follow_up || ''}
                        disabled={savingFollowUp === lead.lead_id}
                        onChange={(v) => updateFollowUp(lead, v)}
                        label="Reschedule"
                        className="w-[112px]"
                      />
                      <button
                        data-testid={`followup-done-${lead.lead_id}`}
                        onClick={() => updateFollowUp(lead, '')}
                        disabled={savingFollowUp === lead.lead_id}
                        title="Mark done (clear follow-up)"
                        className="p-1 rounded text-lime-700 hover:text-lime-900 hover:bg-lime-50 transition disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`followup-open-${lead.lead_id}`}
                        onClick={() => { setNewRemark(''); setDetailDialog({ open: true, lead }); }}
                        title="Open lead"
                        className="p-1 rounded text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 border-b border-stone-300">
            <tr>
              <th className="text-left p-2.5 font-semibold text-stone-700">NAME</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">MOBILE</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">STATUS</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">CITY · PROPERTY</th>
              <th className="text-right p-2.5 font-semibold text-stone-700">BUDGET</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">LATEST REMARK</th>
              <th className="text-center p-2.5 font-semibold text-stone-700">QUOTES</th>
              <th className="text-right p-2.5 font-semibold text-stone-700 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="8" className="p-8 text-center text-stone-400">No leads found.</td></tr>
            ) : (
              filtered.map((lead, idx) => {
                const quoteCount = getQuotationsForLead(lead.lead_id).length;
                const remarks = lead.remarks || [];
                const latest = remarks.length > 0 ? remarks[remarks.length - 1] : null;
                return (
                  <tr key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`} className={`border-b border-stone-100 hover:bg-stone-50 ${idx % 2 ? 'bg-stone-50/50' : ''}`}>
                    <td className="p-2.5 font-medium text-stone-900 align-top">{lead.name}</td>
                    <td className="p-2.5 text-stone-700 tabular-nums align-top">
                      <span className="inline-flex items-center gap-1">
                        {lead.mobile}
                        {lead.is_phone_verified && (
                          <span title="Phone verified" className="inline-block w-3.5 h-3.5 rounded-full bg-lime-500 text-white text-[8px] leading-[14px] text-center font-bold" data-testid={`lead-verified-${lead.lead_id}`}>✓</span>
                        )}
                      </span>
                      <div className="text-[10px] text-stone-400">{lead.created_at?.split('T')[0]}</div>
                    </td>
                    <td className="p-2.5 align-top">
                      <InlineStatusBadge lead={lead} onChangeStatus={updateStatus} />
                      <FollowUpInline
                        lead={lead}
                        saving={savingFollowUp === lead.lead_id}
                        onChange={(d) => updateFollowUp(lead, d)}
                      />
                    </td>
                    <td className="p-2.5 text-stone-700 align-top">
                      <div>{lead.city}<span className="text-stone-400 ml-1">— {lead.pincode}</span></div>
                      <div className="text-[10px] text-stone-500 mt-0.5">{lead.property_type || <span className="text-stone-300">—</span>}</div>
                    </td>
                    <td className="p-2.5 text-stone-700 tabular-nums text-right align-top">{lead.budget != null && lead.budget !== '' ? formatRupeeInt(lead.budget) : <span className="text-stone-300">—</span>}</td>
                    <td className="p-2.5 align-top max-w-xs">
                      {latest ? (
                        <div data-testid={`lead-latest-remark-${lead.lead_id}`}>
                          <div className="text-[11px] text-stone-700 line-clamp-2 leading-snug">{latest.text}</div>
                          <div className="text-[9px] text-stone-400 mt-0.5">{latest.author_name?.split(' ')[0] || 'Admin'} · {fmtDateTime(latest.created_at)}{remarks.length > 1 && <span className="ml-1.5 text-amber-700 font-medium">+{remarks.length - 1} more</span>}</div>
                        </div>
                      ) : (
                        <span className="text-[10px] text-stone-300 italic">No remarks yet</span>
                      )}
                    </td>
                    <td className="p-2.5 text-center align-top">
                      {quoteCount > 0 ? (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-[10px] font-semibold">
                          <FileText className="w-2.5 h-2.5" /> {quoteCount}
                        </span>
                      ) : (
                        <span className="text-stone-400">-</span>
                      )}
                    </td>
                    <td className="p-2.5 text-right align-top">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`build-quote-${lead.lead_id}`}
                          onClick={(e) => { e.stopPropagation(); openQuotationForLead(lead); }}
                          title={quoteCount > 0 ? 'Edit / view quotations' : 'Build a quotation for this lead'}
                          className="p-1.5 rounded text-amber-700 hover:text-amber-900 hover:bg-amber-50 transition"
                        >
                          {quoteCount > 0 ? <Pencil className="w-3.5 h-3.5" /> : <FilePlus2 className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          data-testid={`view-lead-${lead.lead_id}`}
                          onClick={() => { setNewRemark(''); setDetailDialog({ open: true, lead }); }}
                          title="View lead details"
                          className="p-1.5 rounded text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-lead-${lead.lead_id}`}
                          onClick={(e) => { e.stopPropagation(); deleteLead(lead); }}
                          title="Delete lead"
                          className="p-1.5 rounded text-stone-400 hover:text-red-700 hover:bg-red-50 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={v => !v && setDetailDialog({ open: false, lead: null })}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <DialogTitle>Lead Details</DialogTitle>
              {detailDialog.lead && (
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="dialog-build-quote"
                    variant="outline"
                    size="sm"
                    onClick={() => openQuotationForLead(detailDialog.lead)}
                    className="text-amber-800 border-amber-200 hover:bg-amber-50 text-xs h-7"
                  >
                    {getQuotationsForLead(detailDialog.lead.lead_id).length > 0
                      ? <><Pencil className="w-3.5 h-3.5 mr-1" /> Edit Quotation</>
                      : <><FilePlus2 className="w-3.5 h-3.5 mr-1" /> Build Quotation</>}
                  </Button>
                  <Button
                    data-testid="dialog-delete-lead"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteLead(detailDialog.lead, true)}
                    className="text-red-700 hover:text-red-800 hover:bg-red-50 text-xs h-7"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Lead
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {detailDialog.lead && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-2 text-stone-700"><Users className="w-3.5 h-3.5 text-stone-400" /> {detailDialog.lead.name}</div>
                <div className="flex items-center gap-2 text-stone-700"><Phone className="w-3.5 h-3.5 text-stone-400" /> {detailDialog.lead.mobile}{detailDialog.lead.is_phone_verified && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-lime-100 text-lime-700 font-semibold">VERIFIED</span>}</div>
                <div className="flex items-center gap-2 text-stone-700"><MapPin className="w-3.5 h-3.5 text-stone-400" /> {detailDialog.lead.city}, {detailDialog.lead.pincode}</div>
                <div className="flex items-center gap-2 text-stone-700"><Calendar className="w-3.5 h-3.5 text-stone-400" /> {detailDialog.lead.created_at?.split('T')[0]}</div>
                <div className="flex items-center gap-2 text-stone-700 col-span-2"><span className="text-[10px] uppercase tracking-wide font-semibold text-stone-500">Property:</span> {detailDialog.lead.property_type || <span className="text-stone-300">—</span>}<span className="ml-3 text-[10px] uppercase tracking-wide font-semibold text-stone-500">Budget:</span> {detailDialog.lead.budget != null && detailDialog.lead.budget !== '' ? formatRupeeInt(detailDialog.lead.budget) : <span className="text-stone-300">—</span>}<span className="ml-3 text-[10px] uppercase tracking-wide font-semibold text-stone-500">Address:</span> <span data-testid="lead-detail-address" className="break-words">{detailDialog.lead.address || <span className="text-stone-300">—</span>}</span></div>
              </div>

              {/* Status selector */}
              <div className="bg-stone-50 rounded-lg border border-stone-200 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 mb-2">Internal Status</div>
                <div className="flex flex-wrap gap-1.5">
                  {LEAD_STATUSES.map(s => {
                    const active = (detailDialog.lead.internal_status || 'New') === s.value;
                    return (
                      <button
                        key={s.value}
                        data-testid={`dialog-status-${s.key}`}
                        onClick={() => updateStatus(detailDialog.lead, s.value)}
                        disabled={savingStatus}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${active ? `${s.cls} ring-2 ring-offset-1 ring-amber-400` : `${s.cls} opacity-60 hover:opacity-100`}`}
                      >
                        <span>{s.emoji}</span>{s.label}
                      </button>
                    );
                  })}
                </div>
                {/* Next follow-up — relevant to status, hence right under it */}
                <div className="mt-3 pt-3 border-t border-stone-200 flex items-center gap-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Next Follow-up</div>
                  <FollowUpDatePicker
                    testId="dialog-followup-date"
                    value={detailDialog.lead.next_follow_up || ''}
                    onChange={(v) => updateFollowUp(detailDialog.lead, v)}
                    disabled={savingFollowUp === detailDialog.lead.lead_id}
                    label="Set date"
                    className="text-xs"
                  />
                  {detailDialog.lead.next_follow_up && (
                    <button
                      data-testid="dialog-followup-clear"
                      onClick={() => updateFollowUp(detailDialog.lead, '')}
                      className="text-[10px] text-stone-500 hover:text-red-700 underline underline-offset-2"
                    >
                      Clear
                    </button>
                  )}
                  {detailDialog.lead.next_follow_up && (() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const d = detailDialog.lead.next_follow_up;
                    if (d < today) return <span className="text-[10px] font-semibold text-red-700 ml-1">· Overdue</span>;
                    if (d === today) return <span className="text-[10px] font-semibold text-amber-700 ml-1">· Today</span>;
                    return null;
                  })()}
                </div>
              </div>

              {/* Remarks timeline + composer */}
              <div className="bg-white rounded-lg border border-stone-200">
                <div className="px-3 py-2 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Remarks Timeline</span>
                  <span className="text-[10px] text-stone-400">{(detailDialog.lead.remarks || []).length} {((detailDialog.lead.remarks || []).length === 1 ? 'entry' : 'entries')}</span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-stone-100">
                  {(detailDialog.lead.remarks || []).length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-stone-400 italic">No remarks yet. Add the first one below.</div>
                  ) : (
                    [...(detailDialog.lead.remarks || [])].reverse().map((r) => (
                      <div key={r.remark_id} data-testid={`remark-${r.remark_id}`} className="px-3 py-2.5">
                        <div className="text-xs text-stone-800 leading-relaxed whitespace-pre-wrap">{r.text}</div>
                        <div className="text-[10px] text-stone-400 mt-1">{r.author_name || 'Admin'} · {fmtDateTime(r.created_at)}</div>
                      </div>
                    ))
                  )}
                </div>
                <div className="px-3 py-2.5 border-t border-stone-200 bg-stone-50">
                  <textarea
                    data-testid="remark-composer"
                    value={newRemark}
                    onChange={(e) => setNewRemark(e.target.value)}
                    placeholder="Add a remark — what happened in the call/meeting? Next steps?"
                    maxLength={2000}
                    rows={2}
                    className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-stone-400">{newRemark.length}/2000</span>
                    <Button
                      data-testid="add-remark-btn"
                      size="sm"
                      onClick={addRemark}
                      disabled={savingRemark || !newRemark.trim()}
                      className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-7"
                    >
                      <Send className="w-3 h-3 mr-1" /> {savingRemark ? 'Saving...' : 'Add Remark'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quotations */}
              <div>
                <h4 className="text-xs font-semibold text-stone-700 mb-2">Quotations</h4>
                {getQuotationsForLead(detailDialog.lead.lead_id).length === 0 ? (
                  <p className="text-xs text-stone-400">No quotations submitted yet.</p>
                ) : (
                  getQuotationsForLead(detailDialog.lead.lead_id).map(q => {
                    const tmpl = templates.find(t => t.template_id === q.template_id);
                    return (
                      <div key={q.quotation_id} className="bg-stone-50 rounded-lg border border-stone-200 p-3 mb-2">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-semibold text-stone-700">{tmpl?.name || 'Quotation'}</span>
                          <span className="text-[10px] text-stone-400">{q.created_at?.split('T')[0]}</span>
                        </div>
                        <div className="space-y-1">
                          {tmpl?.fields?.filter(f => f.client_visible).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(f => {
                            const val = q.field_values?.[f.field_id];
                            if (val === undefined || val === '' || val === null) return null;
                            return (
                              <div key={f.field_id} className="flex justify-between text-xs">
                                <span className="text-stone-600">{f.label}</span>
                                <span className="font-medium text-stone-800 tabular-nums">
                                  {f.type === 'calculated' ? formatRupeeFixed(val) : String(val)}
                                  {f.unit && f.type !== 'calculated' && ` ${f.unit}`}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {q.room_selections?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-stone-200">
                            <span className="text-[10px] font-semibold text-stone-500">Room Calculator:</span>
                            {q.room_selections.map((rs, i) => (
                              <div key={i} className="flex justify-between text-[10px] text-stone-600 mt-0.5">
                                <span>{rs.name} ({Object.entries(rs.inputs || {}).map(([k, v]) => `${k}: ${v}`).join(', ')})</span>
                                <span className="font-medium">{rs.result} sheets</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog (admin/staff direct entry — no OTP required) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Lead</DialogTitle>
            <p className="text-[11px] text-stone-500">Enter lead details on behalf of the client. Use this for walk-ins, phone enquiries, or referrals.</p>
          </DialogHeader>
          <form onSubmit={submitAddLead} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input data-testid="add-lead-name" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="Full name" required className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Mobile *</Label>
                <Input data-testid="add-lead-mobile" value={addForm.mobile} onChange={e => setAddForm({ ...addForm, mobile: e.target.value })} placeholder="10-digit mobile" required className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">City *</Label>
                <Input data-testid="add-lead-city" value={addForm.city} onChange={e => setAddForm({ ...addForm, city: e.target.value })} placeholder="City" required className="border-stone-200 h-9" />
              </div>
              <div>
                <Label className="text-xs">Pincode *</Label>
                <Input data-testid="add-lead-pincode" value={addForm.pincode} onChange={e => setAddForm({ ...addForm, pincode: e.target.value })} placeholder="6-digit pincode" required className="border-stone-200 h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Address (optional)</Label>
                <textarea
                  data-testid="add-lead-address"
                  value={addForm.address}
                  onChange={e => setAddForm({ ...addForm, address: e.target.value })}
                  placeholder="Flat / House no, Building, Street, Landmark"
                  rows={2}
                  maxLength={500}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                />
              </div>
              <div>
                <Label className="text-xs">Property Type *</Label>
                <Select value={addForm.property_type} onValueChange={(v) => setAddForm({ ...addForm, property_type: v })}>
                  <SelectTrigger data-testid="add-lead-property-type" className="border-stone-200 h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source / Reference</Label>
                <Select value={addForm.source} onValueChange={(v) => setAddForm({ ...addForm, source: v })}>
                  <SelectTrigger data-testid="add-lead-source" className="border-stone-200 h-9"><SelectValue placeholder="How did they hear?" /></SelectTrigger>
                  <SelectContent>
                    {LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Budget (optional)</Label>
                <Input data-testid="add-lead-budget" type="number" min="0" value={addForm.budget} onChange={e => setAddForm({ ...addForm, budget: e.target.value })} placeholder="e.g., 350000" className="border-stone-200 h-9" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Initial Note (optional, becomes first remark)</Label>
                <textarea
                  data-testid="add-lead-note"
                  value={addForm.note}
                  onChange={e => setAddForm({ ...addForm, note: e.target.value })}
                  rows={3}
                  maxLength={2000}
                  placeholder="e.g., Referred by Mr. Suresh from Apt B-204; interested in modular kitchen and false ceiling for 3BHK"
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-xs text-stone-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                />
                <div className="text-[10px] text-stone-400 text-right mt-0.5">{addForm.note.length}/2000</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <Button type="button" variant="outline" size="sm" onClick={() => setAddOpen(false)} className="text-xs h-9">Cancel</Button>
              <Button data-testid="add-lead-submit" type="submit" size="sm" disabled={addSubmitting} className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-9">
                {addSubmitting ? 'Adding…' : 'Add Lead'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
